import type { ParametrosConsulta } from './cliente'
import type { NomeCatalogo } from '@baiocchi/shared'

/**
 * Chaves de cache do `react-query`, em um lugar só.
 *
 * Chave montada na tela é o caminho garantido para invalidação que não funciona:
 * registrar uma interação precisa invalidar o **detalhe**, a **timeline**, a
 * **lista**, o **painel** e o **dashboard** — porque o servidor mexeu em todos
 * eles na mesma transação (`04-leads-workflows.md §4`). Com prefixos declarados
 * aqui, `invalidateQueries({ queryKey: chaves.leads.todos })` alcança tudo.
 */
export const chaves = {
  sessao: ['sessao'] as const,

  leads: {
    todos: ['leads'] as const,
    lista: (filtros?: ParametrosConsulta) => ['leads', 'lista', filtros ?? {}] as const,
    detalhe: (uuid: string) => ['leads', 'detalhe', uuid] as const,
    timeline: (uuid: string, filtros?: ParametrosConsulta) =>
      ['leads', 'timeline', uuid, filtros ?? {}] as const,
    agendamentos: (uuid: string) => ['leads', 'agendamentos', uuid] as const,
  },

  clientes: {
    historico: (uuid: string) => ['clientes', 'historico', uuid] as const,
  },

  painel: {
    todos: ['painel'] as const,
    colunas: (filtros?: ParametrosConsulta) => ['painel', 'colunas', filtros ?? {}] as const,
    cards: (temperatura: string, filtros?: ParametrosConsulta) =>
      ['painel', 'cards', temperatura, filtros ?? {}] as const,
  },

  agendamentos: {
    todos: ['agendamentos'] as const,
    lista: (filtros?: ParametrosConsulta) => ['agendamentos', 'lista', filtros ?? {}] as const,
    calendario: (filtros: ParametrosConsulta) => ['agendamentos', 'calendario', filtros] as const,
  },

  indicadores: {
    todos: ['indicadores'] as const,
    dashboard: (filtros?: ParametrosConsulta) => ['indicadores', 'dashboard', filtros ?? {}] as const,
    funil: (filtros?: ParametrosConsulta) => ['indicadores', 'funil', filtros ?? {}] as const,
    perdas: (filtros?: ParametrosConsulta) => ['indicadores', 'perdas', filtros ?? {}] as const,
  },

  usuarios: {
    todos: ['usuarios'] as const,
    lista: (filtros?: ParametrosConsulta) => ['usuarios', 'lista', filtros ?? {}] as const,
    detalhe: (uuid: string) => ['usuarios', 'detalhe', uuid] as const,
  },

  organizacao: {
    unidades: (filtros?: ParametrosConsulta) => ['organizacao', 'unidades', filtros ?? {}] as const,
    equipes: (filtros?: ParametrosConsulta) => ['organizacao', 'equipes', filtros ?? {}] as const,
    membros: (equipeId: number) => ['organizacao', 'membros', equipeId] as const,
    atendentesElegiveis: (equipeId: number | null, paraDistribuicao: boolean) =>
      ['organizacao', 'atendentes-elegiveis', equipeId, paraDistribuicao] as const,
  },

  catalogos: {
    todos: ['catalogos'] as const,
    lista: (catalogo: NomeCatalogo, filtros?: ParametrosConsulta) =>
      ['catalogos', catalogo, filtros ?? {}] as const,
    ativos: (catalogo: NomeCatalogo) => ['catalogos', catalogo, 'ativos'] as const,
  },

  notificacoes: {
    todos: ['notificacoes'] as const,
    contador: ['notificacoes', 'contador'] as const,
  },

  /**
   * Meta Ads. `todos` é o prefixo de invalidação: executar uma ação ou sincronizar mexe
   * no painel, na hierarquia, no diagnóstico e nas recomendações ao mesmo tempo — e
   * `invalidateQueries({ queryKey: chaves.metaAds.todos })` alcança os quatro.
   */
  metaAds: {
    todos: ['meta-ads'] as const,
    contas: ['meta-ads', 'contas'] as const,
    painel: (filtros?: ParametrosConsulta) => ['meta-ads', 'painel', filtros ?? {}] as const,
    hierarquia: (filtros?: ParametrosConsulta) =>
      ['meta-ads', 'hierarquia', filtros ?? {}] as const,
    diagnostico: (filtros?: ParametrosConsulta) =>
      ['meta-ads', 'diagnostico', filtros ?? {}] as const,
    comparacao: (filtros?: ParametrosConsulta) =>
      ['meta-ads', 'comparacao', filtros ?? {}] as const,
    recomendacoes: (filtros?: ParametrosConsulta) =>
      ['meta-ads', 'recomendacoes', filtros ?? {}] as const,
    otimizacoes: (filtros?: ParametrosConsulta) =>
      ['meta-ads', 'otimizacoes', filtros ?? {}] as const,
    variacoes: (filtros?: ParametrosConsulta) => ['meta-ads', 'variacoes', filtros ?? {}] as const,
  },

  auditoria: (filtros?: ParametrosConsulta) => ['auditoria', filtros ?? {}] as const,
} as const
